import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '@/components/ui/badge';
import { useBaseForecast } from '@/hooks/useBaseForecast';
import { useFormatting } from '@/hooks/useFormatting';
import { useVisibleScenarios } from '@/hooks/useVisibleScenarios';
import { useScenarioForecasts } from '@/hooks/useScenarioForecasts';
import { useApp } from '@/contexts/AppContext';
import { applyScenarioToForecast } from '@/lib/scenarioEngine';
import type { ProjectedForecastPoint } from '@/lib/scenarioEngine';
import {
  getCashDurationShortTerm,
  getCashDurationTerm,
  getForecastTerm,
} from '@/lib/terminology-config';
import { getCurrencySymbol } from '@/utils/currency';

const SCENARIO_COLORS = ['text-red-600', 'text-primary', 'text-green-600'];
const BADGE_VARIANTS: ('destructive' | 'secondary' | 'default')[] = ['destructive', 'secondary', 'default'];
const CHART_STROKES = ['#ef4444', 'hsl(var(--primary))', '#22c55e'];
const CHART_DASH = ['4 2', undefined, undefined];

export const ScenarioComparisonPreview: React.FC = () => {
  const { formatCurrency } = useFormatting();
  const forecastTerm = getForecastTerm(true);
  const { mode } = useApp();
  const isLive = mode === 'live';

  const { baseForecast: baseData } = useBaseForecast();
  const visibleScenarios = useVisibleScenarios();

  // Pick up to 3 scenarios for the comparison report
  const selectedScenarios = useMemo(
    () => visibleScenarios.slice(0, 3),
    [visibleScenarios],
  );

  const selectedIds = useMemo(() => selectedScenarios.map(s => s.id), [selectedScenarios]);
  const { data: scenarioForecastData } = useScenarioForecasts(selectedIds, '90d');

  // Compute projected forecasts for each selected scenario
  const projectedForecasts = useMemo(() => {
    if (isLive) {
      const map = new Map<string, ProjectedForecastPoint[]>();
      for (const sc of selectedScenarios) {
        const forecast = scenarioForecastData[sc.id];
        if (forecast) {
          map.set(sc.id, forecast.projectedForecast.map(p => ({
            date: new Date(p.date),
            baseBalance: p.baseBalance,
            scenarioBalance: p.scenarioBalance,
            baseIncoming: p.baseIncoming,
            baseOutgoing: p.baseOutgoing,
            scenarioIncoming: p.scenarioIncoming,
            scenarioOutgoing: p.scenarioOutgoing,
            delta: p.delta,
          })));
        }
      }
      return map;
    }

    if (baseData.length === 0) return new Map<string, ProjectedForecastPoint[]>();
    const map = new Map<string, ProjectedForecastPoint[]>();
    for (const sc of selectedScenarios) {
      if (sc.changes.length === 0) {
        map.set(sc.id, baseData.map(p => ({
          date: p.date,
          baseBalance: p.balance,
          scenarioBalance: p.balance,
          baseIncoming: p.incoming,
          baseOutgoing: p.outgoing,
          scenarioIncoming: p.incoming,
          scenarioOutgoing: p.outgoing,
          delta: 0,
        })));
      } else {
        const result = applyScenarioToForecast(baseData, sc.changes);
        map.set(sc.id, result.projectedForecast);
      }
    }
    return map;
  }, [baseData, selectedScenarios, isLive, scenarioForecastData]);

  // Build chart data — weekly points
  const chartData = useMemo(() => {
    if (baseData.length === 0) return [];
    return baseData
      .filter((_, i) => i % 7 === 0 || i === baseData.length - 1)
      .map((d, wi) => {
        const idx = Math.min(wi * 7, baseData.length - 1);
        const point: Record<string, string | number> = {
          date: d.date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
          }),
          base: Math.round(baseData[idx]?.balance ?? 0),
        };
        for (const sc of selectedScenarios) {
          const forecast = projectedForecasts.get(sc.id);
          point[sc.id] = Math.round(forecast?.[idx]?.scenarioBalance ?? 0);
        }
        return point;
      });
  }, [baseData, selectedScenarios, projectedForecasts]);

  // Build scenario display rows for KPI cards
  const scenarioDisplayRows = useMemo(() => {
    return selectedScenarios.map((sc, i) => {
      const forecast = projectedForecasts.get(sc.id) ?? [];
      const balances = forecast.map(p => p.scenarioBalance);
      return {
        id: sc.id,
        name: sc.name,
        color: SCENARIO_COLORS[i % SCENARIO_COLORS.length],
        badgeVariant: BADGE_VARIANTS[i % BADGE_VARIANTS.length],
        description: sc.description,
        runway: sc.metrics?.runway,
        closing: Math.round(balances[balances.length - 1] ?? 0),
        minBalance: balances.length > 0 ? Math.min(...balances) : 0,
        minDay: balances.length > 0 ? balances.indexOf(Math.min(...balances)) : 0,
      };
    });
  }, [selectedScenarios, projectedForecasts]);

  const openingBalance = baseData.length > 0 ? Math.round(baseData[0].balance) : 0;

  return (
    <div className="space-y-6">
      {/* Scenario KPI comparison */}
      <div className="grid grid-cols-3 gap-4">
        {scenarioDisplayRows.map((s) => (
          <div key={s.id} className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className={`font-semibold ${s.color}`}>{s.name}</p>
              <Badge variant={s.badgeVariant}>
                {s.runway != null ? getCashDurationTerm(s.runway) : '—'}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">
                Closing Balance
              </p>
              <p className="text-lg font-bold">{formatCurrency(s.closing)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Minimum Balance</p>
              <p className="text-base font-semibold">
                {formatCurrency(s.minBalance)}
              </p>
              <p className="text-xs text-muted-foreground">Day {s.minDay}</p>
            </div>
            <p className="text-xs text-muted-foreground border-t pt-2">
              {s.description}
            </p>
          </div>
        ))}
      </div>

      {/* Overlay chart */}
      <div>
        <p className="text-sm font-semibold mb-3">
          Balance — All {forecastTerm} Scenarios
        </p>
        <div className="h-60 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                interval={1}
                className="fill-muted-foreground"
              />
              <YAxis
                tickFormatter={(v) =>
                  `${getCurrencySymbol()}${(v / 1000).toFixed(0)}k`
                }
                tick={{ fontSize: 10 }}
                className="fill-muted-foreground"
              />
              <Tooltip
                formatter={
                  ((value: number, name: string) => [
                    formatCurrency(value),
                    name.charAt(0).toUpperCase() + name.slice(1),
                  ]) as never
                }
                contentStyle={{ fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="base"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={false}
                name="Base"
              />
              {selectedScenarios.map((sc, i) => (
                <Line
                  key={sc.id}
                  type="monotone"
                  dataKey={sc.id}
                  stroke={CHART_STROKES[i % CHART_STROKES.length]}
                  strokeWidth={2}
                  dot={false}
                  name={sc.name}
                  strokeDasharray={CHART_DASH[i % CHART_DASH.length]}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Key differences table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/60">
              <th className="px-4 py-3 text-left font-semibold">Metric</th>
              {scenarioDisplayRows.map((s) => (
                <th key={s.id} className={`px-4 py-3 text-right font-semibold ${s.color}`}>
                  {s.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b hover:bg-muted/30 transition-colors">
              <td className="px-4 py-2.5 font-medium">Opening Balance</td>
              {scenarioDisplayRows.map((s) => (
                <td key={s.id} className="px-4 py-2.5 text-right">
                  {formatCurrency(openingBalance)}
                </td>
              ))}
            </tr>
            <tr className="border-b hover:bg-muted/30 transition-colors">
              <td className="px-4 py-2.5 font-medium">Closing Balance</td>
              {scenarioDisplayRows.map((s) => (
                <td key={s.id} className="px-4 py-2.5 text-right">
                  {formatCurrency(s.closing)}
                </td>
              ))}
            </tr>
            <tr className="border-b hover:bg-muted/30 transition-colors">
              <td className="px-4 py-2.5 font-medium">
                Cash {getCashDurationShortTerm().charAt(0).toUpperCase() + getCashDurationShortTerm().slice(1)}
              </td>
              {scenarioDisplayRows.map((s) => (
                <td key={s.id} className="px-4 py-2.5 text-right">
                  {s.runway != null ? getCashDurationTerm(s.runway) : '—'}
                </td>
              ))}
            </tr>
            <tr className="border-b hover:bg-muted/30 transition-colors">
              <td className="px-4 py-2.5 font-medium">Minimum Balance</td>
              {scenarioDisplayRows.map((s) => (
                <td key={s.id} className="px-4 py-2.5 text-right">
                  {formatCurrency(s.minBalance)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
